import type { FieldFormatter, Function } from '@lucidtech/las-sdk-browser';
import { useCallback, useMemo } from 'react';

import { useBatchGetFile, useBatchGetFunctions } from '@/hooks/api';
import { selectFromFieldConfig, useDocumentStore } from '@/store';
import { FieldContextType, FieldStateValueType, FormatterFnType, importJS } from '@/utils';

type FormatterResolved = FieldFormatter & {
  function?: Function;
  transform?: FormatterFnType;
};

export type UseFormattersOpts = {
  name: string;
};

export type FormattersFnReturnType = {
  result?: FieldStateValueType;
  meta: { id: string; warnings: string[]; errors: string[] }[];
};

export const useFormatters = ({ name }: UseFormattersOpts) => {
  const config = useDocumentStore(selectFromFieldConfig(name));
  const { data: functions } = useBatchGetFunctions(config?.formatters?.map(({ functionId }) => ({ functionId })));
  const { data: formatterBlobs } = useBatchGetFile(functions?.map((f) => ({ fileUrl: f.fileUrl })));

  const formatters = useMemo(async () => {
    const formatters: FormatterResolved[] = config?.formatters?.map((f) => ({ ...f })) ?? [];

    if (formatters.length === functions.length) {
      for (const [i, formatter] of formatters.entries()) {
        formatter.function = functions[i];
        const formatterBlob = formatterBlobs[i];
        if (!formatterBlob) continue;
        formatter.transform = (await importJS(formatterBlob)).default;
      }
    }
    return formatters;
  }, [formatterBlobs, config?.formatters, functions]);

  const run = useCallback(
    async (state: FieldStateValueType, context: FieldContextType): Promise<FormattersFnReturnType> => {
      let newState: FieldStateValueType | undefined = state;
      const meta = [];

      const fmts = await formatters;

      if (fmts.length > 0) {
        for (const formatter of await formatters) {
          if (!formatter) continue;

          const formatterContext = structuredClone(context);
          formatterContext.config = structuredClone(formatter.config);
          if (!formatter.transform) continue;

          const id = formatter.id;

          try {
            const {
              result,
              errors: newErrors,
              warnings: newWarnings,
            } = formatter.transform({ event: structuredClone(newState ?? {}), context: formatterContext });

            newState = result;
            meta.push({ id, warnings: newWarnings ?? [], errors: newErrors ?? [] });

            if ((newErrors ?? []).length > 0) return { meta };
          } catch (error) {
            if (error instanceof Error) {
              if (context.config.validationMode === 'error') {
                meta.push({ id, warnings: [], errors: [error.message] });
              } else {
                meta.push({ id, warnings: [error.message], errors: [] });
              }
            }

            return { meta };
          }
        }
      } else {
        // If no formatters have run => value should be = rawValue
        if (!newState.value) {
          newState.value = newState.rawValue;
        }
      }

      return { result: newState, meta };
    },
    [formatters]
  );

  return { run };
};
